import { formatNumber } from "../../utils/format";

import type { UsdvMigrationCardState, UsdvMigrationPlan } from "./types";

function hasAmount(amount: UsdvMigrationPlan["legacyUsdv"] | null | undefined): boolean {
    return amount?.gt(0) ?? false;
}

/**
 * The migration pipeline, in order. Every balance is legacy value at some
 * point along it, so the migration is one walk rather than several flows:
 *
 *   1. Unstake legacy sUSDv
 *   2. Claim unstaked legacy USDv
 *   3. Swap legacy USDv to USDC
 *   4. Swap USDC to new USDv
 *
 * A balance ENTERS at the step that applies to it and walks everything after,
 * so a shorter path is a suffix of a longer one. Steps drop out when they are
 * not needed: legacy USDv already in the wallet starts at 3, so it migrates in
 * two steps, not four. USDC left mid-walk by step 3 enters at 4, which is what
 * keeps the last step reachable once the legacy balance is gone.
 */
export const USDV_MIGRATION_STEPS = [
    "Unstake Legacy sUSDv",
    "Claim Unstaked Legacy USDv",
    "Swap Legacy USDv to USDC",
    "Swap USDC to New USDv"
] as const;

/** 1-based index into USDV_MIGRATION_STEPS where each balance joins the walk. */
const USDV_MIGRATION_ENTRY_UNSTAKE = 1;
const USDV_MIGRATION_ENTRY_CLAIM = 2;
const USDV_MIGRATION_ENTRY_SWAP = 3;
const USDV_MIGRATION_ENTRY_CONVERT = 4;

/**
 * Steps remaining for a plan: the walk from the EARLIEST step any of its
 * balances still needs, through the end. With several balances that is the
 * longest single path, not the sum — unstaking produces the legacy USDv the
 * later steps carry, so the shorter path is absorbed by the longer one.
 */
export function getUsdvMigrationStepCount(plan: UsdvMigrationPlan): number {
    const entries: number[] = [];
    if (plan.stakedUsdv.gt(0)) entries.push(USDV_MIGRATION_ENTRY_UNSTAKE);
    if (hasAmount(plan.coolingUsdv)) entries.push(USDV_MIGRATION_ENTRY_CLAIM);
    if (hasAmount(plan.claimableUsdv)) entries.push(USDV_MIGRATION_ENTRY_CLAIM);
    if (plan.legacyUsdv.gt(0)) entries.push(USDV_MIGRATION_ENTRY_SWAP);
    if (hasAmount(plan.usdcUsdv)) entries.push(USDV_MIGRATION_ENTRY_CONVERT);
    if (entries.length === 0) return 0;
    return (plan.directSwap ? 3 : USDV_MIGRATION_STEPS.length) - Math.min(...entries) + 1;
}

export interface UsdvMigrationStep {
    /** 1-based position in USDV_MIGRATION_STEPS. */
    index: number;
    /**
     * Steps left, this one included. The flow counts position against the
     * most this has been since it opened, so "Step 2/2" follows "Step 1/2".
     */
    total: number;
    label: (typeof USDV_MIGRATION_STEPS)[number];
    /** Short heading for the step screen. */
    title: (typeof USDV_MIGRATION_STEP_TITLES)[number];
    /** The verb on the step's action button. */
    action: string;
    /** The balance this step moves. */
    amount: UsdvMigrationPlan["legacyUsdv"];
    /** Claim is reachable only once the cooldown has elapsed. */
    waiting: boolean;
}

const USDV_MIGRATION_STEP_ACTIONS = ["Unstake", "Claim", "Swap", "Swap"] as const;

/**
 * Heading for each step. Short on purpose: USDV_MIGRATION_STEPS names the
 * pipeline in full for the model, but the popup is 336px and the heading runs
 * at the h1 scale, where the full names wrap to two lines.
 */
const USDV_MIGRATION_STEP_TITLES = ["Unstake", "Claim", "Swap to USDC", "Swap to USDv"] as const;

/**
 * The step the wallet is on: the earliest step any of its balances still
 * needs. Returns null when there is nothing left to migrate.
 */
export function getUsdvMigrationCurrentStep(plan: UsdvMigrationPlan): UsdvMigrationStep | null {
    const total = getUsdvMigrationStepCount(plan);
    if (total === 0) return null;
    // Pending withdrawals stay on Claim until they can be claimed; swaps follow Claim.
    const index = plan.stakedUsdv.gt(0)
        ? USDV_MIGRATION_ENTRY_UNSTAKE
        : hasAmount(plan.claimableUsdv) || hasAmount(plan.coolingUsdv)
          ? USDV_MIGRATION_ENTRY_CLAIM
          : hasAmount(plan.usdcUsdv)
            ? USDV_MIGRATION_ENTRY_CONVERT
            : plan.legacyUsdv.gt(0)
              ? USDV_MIGRATION_ENTRY_SWAP
              : USDV_MIGRATION_ENTRY_CLAIM;

    const amount =
        index === USDV_MIGRATION_ENTRY_UNSTAKE
            ? plan.stakedUsdv
            : index === USDV_MIGRATION_ENTRY_CLAIM
              ? hasAmount(plan.claimableUsdv)
                  ? plan.claimableUsdv!
                  : (plan.coolingUsdv ?? plan.legacyUsdv)
              : index === USDV_MIGRATION_ENTRY_SWAP
                ? plan.legacyUsdv
                : (plan.usdcUsdv ?? plan.legacyUsdv);

    return {
        index,
        total,
        label: USDV_MIGRATION_STEPS[index - 1],
        title: plan.directSwap && index === 3 ? "Swap to USDv" : USDV_MIGRATION_STEP_TITLES[index - 1],
        action: USDV_MIGRATION_STEP_ACTIONS[index - 1],
        amount,
        waiting: index === USDV_MIGRATION_ENTRY_CLAIM && !hasAmount(plan.claimableUsdv) && hasAmount(plan.coolingUsdv)
    };
}

export function getUsdvMigrationAction(plan: UsdvMigrationPlan): string | null {
    const hasLegacy = plan.legacyUsdv.gt(0);
    const hasStaked = plan.stakedUsdv.gt(0);
    const hasClaimable = hasAmount(plan.claimableUsdv);

    if (hasClaimable && hasStaked) return "Claim, Upgrade & Unstake";
    if (hasLegacy && hasStaked) return "Upgrade & Unstake";
    if (hasClaimable) return "Claim & Upgrade";
    if (hasLegacy) return "Upgrade";
    if (hasStaked) return "Unstake";
    if (hasAmount(plan.coolingUsdv)) return "Claim & Upgrade";
    if (hasAmount(plan.usdcUsdv)) return "Upgrade";
    return null;
}

function getUsdvMigrationRetryCopy(
    state: Extract<UsdvMigrationCardState, { phase: "error" }>,
    plan: UsdvMigrationPlan
): {
    title: string;
    action: string;
} {
    const hasRemainingUnstake = plan.stakedUsdv.gt(0);

    // The pipeline settles one stage at a time — Claim, then Upgrade, then
    // Unstake — so exactly one of them is what failed. The title names that
    // stage; the action still describes everything the retry will carry.
    switch (state.retry) {
        case "legacy": {
            const action = hasRemainingUnstake ? "Upgrade & Unstake" : "Upgrade";
            return { title: "Swap failed", action: `Retry ${action}` };
        }
        case "staked":
            return { title: "Unstake failed", action: "Retry Unstake" };
        case "claim": {
            const action = hasRemainingUnstake ? "Claim, Upgrade & Unstake" : "Claim & Upgrade";
            return { title: "Claim failed", action: `Retry ${action}` };
        }
        case "all": {
            const action = getUsdvMigrationAction(plan) ?? "Upgrade & Unstake";
            // Nothing settled, so the failure is the first stage this plan
            // would have entered.
            const failed = hasAmount(plan.claimableUsdv) ? "Claim" : plan.legacyUsdv.gt(0) ? "Swap" : "Unstake";
            return { title: `${failed} failed`, action: `Retry ${action}` };
        }
    }
}

export function getUsdvMigrationPostClaimRows(
    state: Extract<UsdvMigrationCardState, { phase: "error" }>
): Array<{ label: string; amount: string; status: string }> {
    if (!state.claimSucceeded || !state.cooldownEndsAt || !state.cooldownUsdv) return [];

    return [
        {
            label: "Cooling withdrawal",
            amount: formatUsdvMigrationAmount(state.cooldownUsdv),
            status: `Claim ${formatUsdvMigrationDate(state.cooldownEndsAt)}`
        }
    ];
}

export function hasUsdvMigrationBalance(plan: UsdvMigrationPlan): boolean {
    return (
        plan.legacyUsdv.gt(0) ||
        plan.stakedUsdv.gt(0) ||
        hasAmount(plan.claimableUsdv) ||
        hasAmount(plan.coolingUsdv) ||
        hasAmount(plan.usdcUsdv)
    );
}

/** Keep actionable dust visible, then prefer the USDv redemption estimate. */
export function getUsdvMigrationStakedAmount(plan: UsdvMigrationPlan): UsdvMigrationPlan["stakedUsdv"] {
    if (plan.stakedUsdv.gt(0) && plan.stakedUsdv.lt("0.01")) {
        return plan.stakedUsdv;
    }
    return plan.expectedStakedUsdv ?? plan.stakedUsdv;
}

export function formatUsdvMigrationAmount(amount: UsdvMigrationPlan["legacyUsdv"]): string {
    if (amount.gt(0) && amount.lt("0.01")) return "<0.01";
    return formatNumber(amount, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

const MIGRATION_DATE_FORMAT = new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric"
});

export function formatUsdvMigrationDate(date: Date): string {
    return MIGRATION_DATE_FORMAT.format(date);
}

/** Date plus time, for the cooldown tooltip — the row itself carries no date. */
const MIGRATION_DATE_TIME_FORMAT = new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
});

export function formatUsdvMigrationDateTime(date: Date): string {
    return MIGRATION_DATE_TIME_FORMAT.format(date);
}

export function getUsdvMigrationCardCopy(
    state: UsdvMigrationCardState,
    plan: UsdvMigrationPlan
): { title: string; action: string | null } {
    switch (state.phase) {
        case "complete":
            return {
                title: hasAmount(plan.coolingUsdv) ? "Unstake cooldown in progress" : "Upgrade complete",
                action: null
            };
        case "available": {
            const action = getUsdvMigrationAction(plan);
            return {
                title: action
                    ? `${action} available`
                    : hasAmount(plan.coolingUsdv)
                      ? "Unstake cooldown in progress"
                      : "USDv actions available",
                action
            };
        }
        case "running":
            return {
                title: `${
                    getUsdvMigrationAction(plan) ?? (state.action === "claim" ? "Claim & Upgrade" : "Upgrade")
                } in progress`,
                action: null
            };
        case "success": {
            if (state.kind === "claim") {
                return {
                    title: `${getUsdvMigrationAction(plan) ?? "Claim & Upgrade"} complete`,
                    action: null
                };
            }
            if (state.kind === "conversion") {
                return { title: "Upgrade complete", action: null };
            }
            return {
                title:
                    state.cooldownEndsAt && plan.legacyUsdv.gt(0)
                        ? "Upgrade complete, Unstake started"
                        : state.cooldownEndsAt
                          ? "Unstake started"
                          : "Upgrade complete",
                action: null
            };
        }
        case "cooldown":
            return { title: "Unstake cooldown in progress", action: null };
        case "claimable": {
            const action =
                plan.claimableUsdv === undefined
                    ? "Claim & Upgrade"
                    : (getUsdvMigrationAction(plan) ?? "Claim & Upgrade");
            return {
                title: `${action} available`,
                action
            };
        }
        case "error":
            return getUsdvMigrationRetryCopy(state, plan);
    }
}
